import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { api, chatConversationsApi, type ChatConversationDetailResponse, type ChatConversationSessionDto } from '../../../lib/api';
import LoeschenMitGrundDialog, { grundHeader } from '../../../components/ui/loeschen-mit-grund-dialog';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';

type StandortItem = { id: number; name: string };

const toLocal = (value: string | null | undefined): string => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString('de-DE');
};

export default function ChatConversationsPage() {
  const [loading, setLoading] = useState(false);
  const [standorte, setStandorte] = useState<StandortItem[]>([]);
  const [items, setItems] = useState<ChatConversationSessionDto[]>([]);
  const [selected, setSelected] = useState<ChatConversationDetailResponse | null>(null);
  // Einzelnes Gespräch löschen oder maskieren (T-0199).
  const darfEntfernen = usePermission(P.auswertungen.ki_gestuetzte_auswertungen.ki_auswertungen_loeschen);
  const [entfernen, setEntfernen] = useState<'loeschen' | 'maskieren' | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [mode, setMode] = useState<'all' | 'guest' | 'authenticated'>('all');
  const [standortId, setStandortId] = useState<string>('all');
  const [query, setQuery] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const load = async (nextPage = page) => {
    setLoading(true);
    try {
      const [listRes, standorteRes] = await Promise.all([
        chatConversationsApi.list({
          from: from || undefined,
          to: to || undefined,
          mode: mode === 'all' ? undefined : mode,
          standortId: standortId === 'all' ? undefined : Number(standortId),
          query: query || undefined,
          page: nextPage,
          pageSize,
        }),
        api.get<StandortItem[]>('/api/standorte'),
      ]);
      setItems(listRes.items || []);
      setTotal(listRes.pagination?.total || 0);
      setPage(listRes.pagination?.page || nextPage);
      setStandorte(standorteRes || []);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Chat-Konversationen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = async () => {
    setSelected(null);
    await load(1);
  };

  const openDetail = async (sessionId: string) => {
    setLoading(true);
    try {
      const detail = await chatConversationsApi.get(sessionId);
      setSelected(detail);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Detailansicht konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Chat-Konversationen</h1>
        <p className="text-muted-foreground">Übersicht gespeicherter Chat-Sessions mit Filter- und Detailansicht.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>Nach Zeitraum, Modus, Standort und Inhalt filtern.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Von</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={loading} />
            </div>
            <div className="space-y-2">
              <Label>Bis</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={loading} />
            </div>
            <div className="space-y-2">
              <Label>Modus</Label>
              <Select value={mode} onValueChange={(v: 'all' | 'guest' | 'authenticated') => setMode(v)} disabled={loading}>
                <SelectTrigger><SelectValue placeholder="Alle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="guest">Gast</SelectItem>
                  <SelectItem value="authenticated">Eingeloggt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Standort</Label>
              <Select value={standortId} onValueChange={setStandortId} disabled={loading}>
                <SelectTrigger><SelectValue placeholder="Alle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Standorte</SelectItem>
                  {standorte.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Suche</Label>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Textsuche..." disabled={loading} />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => void load(page)} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Neu laden
            </Button>
            <Button onClick={() => void applyFilters()} disabled={loading}>
              <Search className="mr-2 h-4 w-4" />
              Filter anwenden
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Sessions ({total})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.length === 0 ? (
              <div className="text-sm text-muted-foreground">Keine Konversationen gefunden.</div>
            ) : (
              items.map((item) => (
                <button
                  key={item.sessionId}
                  type="button"
                  onClick={() => void openDetail(item.sessionId)}
                  className="w-full rounded-md border p-3 text-left hover:bg-accent/30"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{item.sessionId}</div>
                    <div className="text-xs text-muted-foreground">{item.mode}</div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Nachrichten: {item.messageCount} · Letzte Aktivität: {toLocal(item.lastMessageAt)}
                  </div>
                  {item.lastMessagePreview ? (
                    <div className="mt-2 text-sm line-clamp-2">{item.lastMessagePreview}</div>
                  ) : null}
                </button>
              ))
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={() => void load(Math.max(1, page - 1))} disabled={loading || page <= 1}>
                Zurück
              </Button>
              <div className="text-xs text-muted-foreground">Seite {page} / {totalPages}</div>
              <Button variant="outline" onClick={() => void load(Math.min(totalPages, page + 1))} disabled={loading || page >= totalPages}>
                Weiter
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detailansicht</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selected ? (
              <>
                <div className="rounded-md border p-3 text-sm">
                  <div><span className="font-medium">Session:</span> {selected.session.sessionId}</div>
                  <div><span className="font-medium">Modus:</span> {selected.session.mode}</div>
                  <div><span className="font-medium">Standort-ID:</span> {selected.session.standortId ?? '-'}</div>
                  <div><span className="font-medium">Nutzer-ID:</span> {selected.session.wpUserId ?? '-'}</div>
                  <div><span className="font-medium">Aktualisiert:</span> {toLocal(selected.session.updatedAt)}</div>
                </div>
                {darfEntfernen && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEntfernen('maskieren')}>
                      Inhalte maskieren
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => setEntfernen('loeschen')}>
                      Gespräch löschen
                    </Button>
                  </div>
                )}

                <div className="max-h-[540px] space-y-2 overflow-y-auto rounded-md border p-3">
                  {selected.messages.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Keine Nachrichten in dieser Session.</div>
                  ) : (
                    selected.messages.map((m) => (
                      <div key={m.id} className="rounded-md border p-2">
                        <div className="mb-1 text-xs text-muted-foreground">
                          {m.direction} · {toLocal(m.createdAt)}{m.intent ? ` · intent: ${m.intent}` : ''}
                        </div>
                        <div className="whitespace-pre-wrap text-sm">{m.content}</div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Wähle links eine Session aus.</div>
            )}
          </CardContent>
        </Card>
      </div>
      <LoeschenMitGrundDialog
        offen={entfernen !== null && selected !== null}
        titel={entfernen === 'maskieren' ? 'Inhalte des Gesprächs maskieren?' : 'Gespräch endgültig löschen?'}
        beschreibung={
          entfernen === 'maskieren'
            ? 'Die Nachrichten werden entfernt, Zeitraum und Anzahl bleiben stehen. Bitte gib einen Grund an.'
            : undefined
        }
        onAbbrechen={() => setEntfernen(null)}
        onLoeschen={async (grund) => {
          if (!selected || !entfernen) return;
          await api.delete(
            `/api/chat-conversations/${encodeURIComponent(selected.session.sessionId)}?modus=${entfernen}`,
            grundHeader(grund),
          );
          toast.success(entfernen === 'maskieren' ? 'Inhalte maskiert' : 'Gespräch gelöscht');
          const sessionId = selected.session.sessionId;
          setEntfernen(null);
          if (entfernen === 'maskieren') await openDetail(sessionId);
          else setSelected(null);
          await load(page);
        }}
      />
    </div>
  );
}
